"use client";

import { orpcClient } from "@shared/lib/orpc";
import { Button } from "@ui/components/button";
import { Spinner } from "@ui/components/spinner";
import { ToggleGroup, ToggleGroupItem } from "@ui/components/toggle-group";
import { PauseIcon, PlayIcon } from "lucide-react";
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { clockDuration } from "./meta";

/** Which side of the stereo recording plays: the caller (left), the agent (right) or both. */
type Channel = "caller" | "both" | "agent";

export interface CallPlayerHandle {
	seek: (ms: number) => void;
}

/**
 * The call's recording: play, scrub, and listen to one side. The signed link lasts five
 * minutes, so it is fetched when someone presses play and again if it expired. A keyboard
 * moves the scrubber like any range input; Space on the play button plays and pauses.
 */
export const CallPlayer = forwardRef<
	CallPlayerHandle,
	{
		organizationId: string;
		callId: string;
		durationSeconds: number;
		onTime: (ms: number) => void;
	}
>(function CallPlayer(
	{ organizationId, callId, durationSeconds, onTime },
	ref,
) {
	const audio = useRef<HTMLAudioElement | null>(null);
	const graph = useRef<{
		context: AudioContext;
		source: MediaElementAudioSourceNode;
		splitter: ChannelSplitterNode;
		merger: ChannelMergerNode;
	} | null>(null);
	const expiresAt = useRef(0);
	const pendingSeek = useRef<number | null>(null);
	const [loading, setLoading] = useState(false);
	const [playing, setPlaying] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [current, setCurrent] = useState(0);
	const [duration, setDuration] = useState(durationSeconds);
	const [channel, setChannel] = useState<Channel>("both");

	const ensureSource = useCallback(async () => {
		const element = audio.current;
		if (!element) {
			return false;
		}
		if (element.src && Date.now() < expiresAt.current) {
			return true;
		}
		setLoading(true);
		setError(null);
		try {
			const link = await orpcClient.calls.recordingUrl({
				organizationId,
				callId,
			});
			const at = element.currentTime;
			element.src = link.url;
			expiresAt.current = new Date(link.expiresAt).getTime() - 15_000;
			if (at > 0) {
				element.currentTime = at;
			}
			return true;
		} catch {
			setError("Couldn't load the recording. Try again.");
			return false;
		} finally {
			setLoading(false);
		}
	}, [organizationId, callId]);

	const play = useCallback(async () => {
		if (!(await ensureSource())) {
			return;
		}
		if (pendingSeek.current !== null && audio.current) {
			audio.current.currentTime = pendingSeek.current / 1000;
			pendingSeek.current = null;
		}
		await graph.current?.context.resume();
		await audio.current?.play().catch(() => {
			setError("The browser couldn't play this recording.");
		});
	}, [ensureSource]);

	useImperativeHandle(
		ref,
		() => ({
			seek: (ms: number) => {
				const element = audio.current;
				if (!element) {
					return;
				}
				setCurrent(ms / 1000);
				onTime(ms);
				if (element.src) {
					element.currentTime = ms / 1000;
					void play();
				} else {
					pendingSeek.current = ms;
					void play();
				}
			},
		}),
		[onTime, play],
	);

	// Routing one side to both ears needs Web Audio; it is built on first use only, so
	// "Both" plays straight from the element.
	useEffect(() => {
		const element = audio.current;
		if (!element || (channel === "both" && !graph.current)) {
			return;
		}
		if (!graph.current) {
			const context = new AudioContext();
			graph.current = {
				context,
				source: context.createMediaElementSource(element),
				splitter: context.createChannelSplitter(2),
				merger: context.createChannelMerger(2),
			};
		}
		const { context, source, splitter, merger } = graph.current;
		source.disconnect();
		splitter.disconnect();
		merger.disconnect();
		if (channel === "both") {
			source.connect(context.destination);
			return;
		}
		const side = channel === "caller" ? 0 : 1;
		source.connect(splitter);
		splitter.connect(merger, side, 0);
		splitter.connect(merger, side, 1);
		merger.connect(context.destination);
	}, [channel]);

	useEffect(
		() => () => {
			void graph.current?.context.close();
		},
		[],
	);

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center gap-3">
				<Button
					size="icon"
					variant="outline"
					onClick={() =>
						playing ? audio.current?.pause() : void play()
					}
					aria-label={
						playing ? "Pause the recording" : "Play the recording"
					}
					disabled={loading}
				>
					{loading ? (
						<Spinner />
					) : playing ? (
						<PauseIcon />
					) : (
						<PlayIcon />
					)}
				</Button>
				<input
					type="range"
					min={0}
					max={Math.max(1, duration)}
					step={0.1}
					value={current}
					aria-label="Position in the recording"
					aria-valuetext={`${clockDuration(current)} of ${clockDuration(duration)}`}
					onChange={(e) => {
						const seconds = Number(e.target.value);
						setCurrent(seconds);
						onTime(seconds * 1000);
						if (audio.current?.src) {
							audio.current.currentTime = seconds;
						} else {
							pendingSeek.current = seconds * 1000;
						}
					}}
					className="h-1.5 min-w-0 flex-1 cursor-pointer accent-primary"
				/>
				<span className="shrink-0 text-muted-foreground text-xs tabular">
					{clockDuration(current)} / {clockDuration(duration)}
				</span>
			</div>
			<div className="flex flex-wrap items-center justify-between gap-2">
				<ToggleGroup
					value={[channel]}
					onValueChange={(value) => {
						const next = value[0] as Channel | undefined;
						if (next) {
							setChannel(next);
						}
					}}
					variant="outline"
					size="sm"
					aria-label="Which side to hear"
				>
					<ToggleGroupItem value="caller" className="px-3">
						Caller
					</ToggleGroupItem>
					<ToggleGroupItem value="both" className="px-3">
						Both
					</ToggleGroupItem>
					<ToggleGroupItem value="agent" className="px-3">
						Agent
					</ToggleGroupItem>
				</ToggleGroup>
				{error ? (
					<span role="alert" className="text-destructive text-xs">
						{error}
					</span>
				) : null}
			</div>
			{/* The transcript beside the player is the caption track. */}
			<audio
				ref={audio}
				preload="none"
				onPlay={() => setPlaying(true)}
				onPause={() => setPlaying(false)}
				onEnded={() => setPlaying(false)}
				onLoadedMetadata={(e) => {
					const seconds = e.currentTarget.duration;
					if (Number.isFinite(seconds) && seconds > 0) {
						setDuration(seconds);
					}
				}}
				onTimeUpdate={(e) => {
					setCurrent(e.currentTarget.currentTime);
					onTime(e.currentTarget.currentTime * 1000);
				}}
				className="hidden"
			>
				<track kind="captions" />
			</audio>
		</div>
	);
});
